import { useQuery } from "@tanstack/react-query";
import { Clock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { getPosClient } from "../lib/api";
import { formatCents, formatTime } from "../lib/format";
import { orderStatusLabelKey } from "../lib/orderStatus";
import { useActiveTimezone } from "../lib/useActiveTimezone";
import { useFocusTrap } from "../lib/use-focus-trap";
import { OrderStatusBadge } from "./ui/StatusBadge";
import { InlineSpinner } from "./ui/Spinner";

/**
 * Slide-in panel that shows TODAY's orders for the active scope.
 *
 * Mounts on demand - the sidebar's "View recent Transactions" button
 * toggles ``open``; nothing fetches until the panel is opened the
 * first time. This keeps cold-start cost on /sale at zero db calls.
 *
 * Date filter: today (00:00 → 23:59 in the active membership's
 * location timezone, falling back to the browser tz). Uses the new
 * date_from / date_to params on listOrders (#147).
 */
export function RecentTransactionsPanel({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tz = useActiveTimezone();
  const dialogRef = useFocusTrap<HTMLElement>(open, onClose);

  // Today's start in the active tz. Falls back to browser tz (the
  // formatter does the same fallback) - pre-#142 deploys still work
  // without the timezone field on the profile.
  const todayRange = computeTodayRange(tz);

  const orders = useQuery({
    queryKey: ["orders", "today-panel", todayRange.from],
    queryFn: () =>
      getPosClient().listOrders({
        limit: 50,
        date_from: todayRange.from,
        date_to: todayRange.to,
      }),
    enabled: open,
    staleTime: 30_000,
  });

  if (!open) return null;

  return (
    <>
      {/* Backdrop - click closes. Lives behind the panel; keeps the
          sale screen interactive-looking while the panel is up so
          the operator never feels boxed in. */}
      {/* Backdrop + panel both start at ``top-14`` so the global
          topbar (h-14) stays visible while the drawer is open - same
          treatment as TimelinePaymentDrawer (2026-05-03 dogfood: "all
          drawers should render below the header"). The brand strip
          and scope picker stay reachable so the cashier can switch
          context without dismissing the drawer. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-x-0 bottom-0 top-14 z-40 bg-black/30 sm:bg-black/20"
      />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("recentPanel.title")}
        className="fixed bottom-0 right-0 top-14 z-50 flex w-full max-w-md flex-col border-l border-line bg-surface shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-faint" />
              <h2 className="text-sm font-semibold text-strong">{t("recentPanel.title")}</h2>
            </div>
            <p className="mt-0.5 text-xs text-muted">{t("recentPanel.subtitle")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.cancel")}
            className="rounded p-1.5 text-faint hover:bg-surface-strong hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {orders.isPending ? (
            <div className="flex items-center justify-center py-12 text-xs text-muted">
              <InlineSpinner className="mr-2 h-3 w-3" />
              {t("common.loading")}
            </div>
          ) : orders.isError ? (
            <div className="px-4 py-3 text-xs text-red-600">{t("recentPanel.loadError")}</div>
          ) : !orders.data || orders.data.orders.length === 0 ? (
            <div className="px-4 py-12 text-center text-xs text-muted">
              {t("recentPanel.empty")}
            </div>
          ) : (
            <ol className="divide-y divide-line">
              {orders.data.orders.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/transactions/${o.id}`);
                      onClose();
                    }}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left text-sm hover:bg-surface-muted"
                  >
                    <span className="w-14 shrink-0 text-xs text-muted tabular-nums">
                      {formatTime(o.created_at, tz)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-strong">
                        {o.customer_name || o.external_reference || t("recentPanel.unnamedOrder")}
                      </span>
                      {o.external_reference && o.customer_name && (
                        <span className="block truncate text-[0.6875rem] text-muted">
                          {o.external_reference}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-semibold tabular-nums text-strong">
                        {formatCents(o.total_cents, o.currency)}
                      </span>
                      <OrderStatusBadge
                        status={o.status}
                        label={t(orderStatusLabelKey(o.status))}
                        className="mt-0.5"
                      />
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>

        {orders.data && orders.data.summary.order_count > 0 && (
          <footer className="grid grid-cols-3 gap-1 border-t border-line bg-surface-muted px-4 py-3 text-[0.6875rem]">
            <Stat
              label={t("recentPanel.statOrders")}
              value={String(orders.data.summary.order_count)}
            />
            <Stat
              label={t("recentPanel.statPaid")}
              value={formatCents(orders.data.summary.paid_cents, "USD")}
            />
            <Stat
              label={t("recentPanel.statNet")}
              value={formatCents(orders.data.summary.net_cents, "USD")}
            />
          </footer>
        )}
      </aside>
    </>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <div className="font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 font-semibold tabular-nums text-strong">{value}</div>
    </div>
  );
}

interface DateRange {
  readonly from: string;
  readonly to: string;
}

/** Compute today's start + end in the supplied IANA timezone. Returns
 *  ISO timestamps acceptable to the listOrders ``date_from``/``date_to``
 *  params. Falls back to the browser local tz when ``tz`` is undefined. */
function computeTodayRange(tz: string | undefined): DateRange {
  // The listOrders endpoint accepts a date-only ``date_from`` (which
  // it casts to start-of-day) and bumps a date-only ``date_to`` to
  // end-of-day server-side. We just need to know "what local YYYY-MM-DD
  // is it right now in the location's tz?"
  const localDate = new Date().toLocaleDateString("en-CA", {
    ...(tz ? { timeZone: tz } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return { from: localDate, to: localDate };
}
